import { useEffect, useState, type CSSProperties } from 'react';
import './BrandedSplashScreen.css';
import { VibeMomLogo } from './VibeMomLogo';

type PhraseIconProps = {
  className?: string;
};

type SplashPhrase = {
  text: string;
  accentFrom: string;
  accentTo: string;
  Icon: ({ className }: PhraseIconProps) => JSX.Element;
};

function CloudDownloadIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M8 18H16.2C18.3 18 20 16.4 20 14.4C20 12.6 18.7 11.2 16.8 10.9C16.4 8.6 14.4 7 12 7C9.7 7 7.7 8.5 7.2 10.8C5.3 11.1 4 12.5 4 14.3C4 16.3 5.7 18 8 18Z" />
      <path d="M12 10V16" />
      <path d="M9.7 13.7L12 16L14.3 13.7" />
    </svg>
  );
}

function PotCodeIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5 10.5H19L17.8 18H6.2L5 10.5Z" />
      <path d="M7 9C7 8.2 7.6 7.5 8.4 7.5H15.6C16.4 7.5 17 8.2 17 9V10.5H7V9Z" />
      <path d="M9.2 5.5L8 6.7L9.2 7.9" />
      <path d="M14.8 5.5L16 6.7L14.8 7.9" />
      <path d="M11.3 5L10.8 8.4" />
    </svg>
  );
}

function CommitCheckIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="7" cy="7" r="2.4" />
      <circle cx="17" cy="17" r="2.4" />
      <path d="M9.3 7H12.5C14.2 7 15.6 8.4 15.6 10.1V12.8" />
      <path d="M15.5 15.6L16.8 16.9L19.2 14.5" />
      <path d="M7.1 9.4V13C7.1 15.2 8.8 17 11 17H14.4" />
    </svg>
  );
}

function DeployRocketIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M11.3 4.1C13.6 4.5 16.3 5.8 18 7.5C19.6 9.1 20.9 11.8 21.3 14.1L15.8 15.8L9.6 9.6L11.3 4.1Z" />
      <path d="M9.6 9.6L6.2 13C5.4 13.8 5 14.8 5 15.9V18.9H8.1C9.2 18.9 10.2 18.5 11 17.7L14.4 14.3" />
      <circle cx="14.8" cy="9.3" r="1.3" />
      <path d="M5.2 18.8L3.7 20.3" />
    </svg>
  );
}

function BowlCodeIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5.5 12.5H18.5C18.5 15.8 15.8 18.5 12.5 18.5H11.5C8.2 18.5 5.5 15.8 5.5 12.5Z" />
      <path d="M4.5 12.5H19.5" />
      <path d="M8 8.2L6.8 9.4L8 10.6" />
      <path d="M15.9 8.2L17.1 9.4L15.9 10.6" />
      <path d="M12.6 7.7L12.1 11" />
    </svg>
  );
}

function CompileAdviceIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5 6.5H19V14.5C19 16.2 17.6 17.5 15.9 17.5H11.3L8.4 20V17.5H8.1C6.4 17.5 5 16.2 5 14.5V6.5Z" />
      <path d="M9 10.5H15" />
      <path d="M9.2 13H13.8" />
      <circle cx="16.8" cy="8.8" r="1.2" />
      <path d="M16.8 6.8V6" />
      <path d="M18.2 7.4L18.8 6.8" />
      <path d="M18.8 8.8H19.6" />
    </svg>
  );
}

function DependencySyncIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="7" cy="8" r="2.2" />
      <circle cx="17" cy="8" r="2.2" />
      <circle cx="12" cy="17" r="2.2" />
      <path d="M9 8H14.8" />
      <path d="M8.2 10L10.8 14.5" />
      <path d="M15.8 10L13.2 14.5" />
      <path d="M17 5.1L18.9 7L17 8.9" />
      <path d="M7 10.9L5.1 9L7 7.1" />
    </svg>
  );
}

function HatLoaderIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M5 13.5C5 10.7 7.2 8.5 10 8.5H14C16.8 8.5 19 10.7 19 13.5V14.2H5V13.5Z" />
      <path d="M4.2 14.2H19.8" />
      <path d="M12 4.5V8.5" />
      <path d="M9.8 6.2L12 8.5L14.2 6.2" />
      <path d="M8 17.5H16" />
    </svg>
  );
}

function RefactorUniverseIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="3.5" />
      <ellipse cx="12" cy="12" rx="8" ry="3.2" />
      <path d="M18.2 15.5L20.5 17.8" />
      <path d="M16.8 16.9L18.3 15.4L21 18.1L19.5 19.6L16.8 16.9Z" />
      <path d="M10.5 12H13.5" />
      <path d="M12 10.5V13.5" />
    </svg>
  );
}

function ApprovalSealIcon({ className }: PhraseIconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="12" cy="10" r="4.8" />
      <path d="M10.2 10L11.6 11.4L14.2 8.8" />
      <path d="M9.2 14.1V19L12 17.4L14.8 19V14.1" />
      <path d="M6.2 7.2L5.2 6.2" />
      <path d="M17.8 7.2L18.8 6.2" />
    </svg>
  );
}

const SPLASH_PHRASES: SplashPhrase[] = [
  {
    text: 'Мама уже загружает...',
    accentFrom: 'rgba(125, 102, 255, 0.42)',
    accentTo: 'rgba(96, 165, 250, 0.28)',
    Icon: CloudDownloadIcon,
  },
  {
    text: 'Подождите, мама варит код...',
    accentFrom: 'rgba(168, 85, 247, 0.4)',
    accentTo: 'rgba(244, 114, 182, 0.28)',
    Icon: PotCodeIcon,
  },
  {
    text: 'Мама проверяет ваш коммит...',
    accentFrom: 'rgba(99, 102, 241, 0.38)',
    accentTo: 'rgba(192, 132, 252, 0.3)',
    Icon: CommitCheckIcon,
  },
  {
    text: 'Мама деплоит с любовью...',
    accentFrom: 'rgba(236, 72, 153, 0.34)',
    accentTo: 'rgba(167, 139, 250, 0.3)',
    Icon: DeployRocketIcon,
  },
  {
    text: 'Мама говорит: «Ешь, потом кодь»',
    accentFrom: 'rgba(251, 191, 36, 0.35)',
    accentTo: 'rgba(244, 114, 182, 0.28)',
    Icon: BowlCodeIcon,
  },
  {
    text: 'Компилируем мамины советы...',
    accentFrom: 'rgba(52, 211, 153, 0.32)',
    accentTo: 'rgba(125, 211, 252, 0.28)',
    Icon: CompileAdviceIcon,
  },
  {
    text: 'Мама обновляет зависимости...',
    accentFrom: 'rgba(96, 165, 250, 0.32)',
    accentTo: 'rgba(129, 140, 248, 0.3)',
    Icon: DependencySyncIcon,
  },
  {
    text: '«Надень шапку» — загружаем...',
    accentFrom: 'rgba(34, 197, 94, 0.28)',
    accentTo: 'rgba(167, 139, 250, 0.32)',
    Icon: HatLoaderIcon,
  },
  {
    text: 'Мама рефакторит вселенную...',
    accentFrom: 'rgba(56, 189, 248, 0.3)',
    accentTo: 'rgba(168, 85, 247, 0.3)',
    Icon: RefactorUniverseIcon,
  },
  {
    text: 'Получаем одобрение мамы...',
    accentFrom: 'rgba(244, 114, 182, 0.32)',
    accentTo: 'rgba(249, 168, 212, 0.28)',
    Icon: ApprovalSealIcon,
  },
];

export function BrandedSplashScreen() {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(true);
  const [progress, setProgress] = useState(5);
  const [activeDot, setActiveDot] = useState(0);

  useEffect(() => {
    let phraseSwitchTimer: number | null = null;
    const phraseRotation = window.setInterval(() => {
      setPhraseVisible(false);
      phraseSwitchTimer = window.setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % SPLASH_PHRASES.length);
        setPhraseVisible(true);
      }, 260);
    }, 2400);

    return () => {
      window.clearInterval(phraseRotation);
      if (phraseSwitchTimer !== null) window.clearTimeout(phraseSwitchTimer);
    };
  }, []);

  useEffect(() => {
    const progressTimer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        const remaining = 100 - prev;
        const baseStep = remaining > 42 ? 1.45 : remaining > 18 ? 0.86 : 0.32;
        const randomJitter = Math.random() * 0.4;
        return Math.min(100, prev + baseStep + randomJitter);
      });
    }, 140);

    return () => window.clearInterval(progressTimer);
  }, []);

  useEffect(() => {
    const dotsTimer = window.setInterval(() => {
      setActiveDot((prev) => (prev + 1) % 3);
    }, 780);

    return () => window.clearInterval(dotsTimer);
  }, []);

  const currentPhrase = SPLASH_PHRASES[phraseIndex];
  const PhraseIcon = currentPhrase.Icon;

  const accentStyle = {
    '--vm-icon-accent-from': currentPhrase.accentFrom,
    '--vm-icon-accent-to': currentPhrase.accentTo,
  } as CSSProperties;

  return (
    <section className="vm-splash" role="status" aria-live="polite" aria-atomic="true">
      <div className="vm-splash-atmosphere" aria-hidden="true">
        <span className="vm-splash-particle vm-splash-particle-a" />
        <span className="vm-splash-particle vm-splash-particle-b" />
        <span className="vm-splash-particle vm-splash-particle-c" />
        <span className="vm-splash-particle vm-splash-particle-d" />
        <span className="vm-splash-particle vm-splash-particle-e" />
      </div>

      <div className="vm-splash-center">
        <div className="vm-splash-logo-wrap" aria-hidden="true">
          <div className="vm-splash-logo-orbit">
            <span className="vm-splash-logo-orbit-dot" />
          </div>
          <div className="vm-splash-logo-card">
            <div className="vm-splash-logo">
              <VibeMomLogo size={86} />
            </div>
          </div>
        </div>

        <div className="vm-splash-title">МАМ, Я НАВАЙБКОДИЛ</div>

        <div className="vm-splash-phrase-shell">
          <div className={`vm-splash-phrase-row${phraseVisible ? ' is-visible' : ' is-hidden'}`}>
            <div className="vm-splash-phrase-icon-card" style={accentStyle} aria-hidden="true">
              <PhraseIcon className="vm-splash-phrase-icon" />
            </div>
            <p className="vm-splash-phrase-text">{currentPhrase.text}</p>
          </div>
        </div>

        <div className="vm-splash-progress">
          <div className="vm-splash-progress-track">
            <div className="vm-splash-progress-fill" style={{ width: `${Math.round(progress)}%` }} />
          </div>
          <div className="vm-splash-progress-text">{Math.round(progress)}%</div>
        </div>

        <div className="vm-splash-dots" aria-hidden="true">
          <span className={`vm-splash-dot${activeDot === 0 ? ' is-active' : ''}`} />
          <span className={`vm-splash-dot${activeDot === 1 ? ' is-active' : ''}`} />
          <span className={`vm-splash-dot${activeDot === 2 ? ' is-active' : ''}`} />
        </div>
      </div>

      <div className="vm-splash-pill">маркетплейс вайб-кодеров</div>
    </section>
  );
}
